<script setup>
import { computed, ref, onMounted, onUnmounted } from "vue";
import { useRouter } from "vue-router";
import { ROUTE_NAMES } from "Admin/js/Constants/routes";
import { useMobileWorkday } from "Admin/js/Mobile/Composables/useMobileWorkday";
import { useMobileUser } from "Admin/js/Mobile/Composables/useMobileUser";
import KProfileMobileMenu from "js/Admin/js/Components/Common/K/KProfileMobileMenu.vue";
import KDivider from "Admin/js/Components/Common/K/KDivider.vue";
import { useMobileTour } from "Admin/js/Mobile/Composables/useMobileTour";
import MobileOptionsMenuItem from "Admin/js/Mobile/Components/Common/MobileOptionsMenuItem.vue";
import { OFFICE_HOUR_STATUS } from "Admin/js/Constants/workday";
import OfficeOpenIcon from "Admin/js/Components/Icons/OfficeOpenIcon.vue";
import OfficeCloseIcon from "Admin/js/Components/Icons/OfficeCloseIcon.vue";
import PastOfficeHourIcon from "Admin/js/Components/Icons/PastOfficeHourIcon.vue";
import KMobileDialog from "Admin/js/Components/Common/Dialog/KMobileDialog.vue";
import { MOBILE_ANALYTICS_TYPES } from "Admin/js/Constants/menus";
import MobileAnalyticsTracker from "Admin/js/Utilities/MobileAnalyticsTracker";

const router = useRouter();
const {
    officeHourStatus,
    isShiftPausedOrUnreachableOrInActive,
    shiftInfo,
    isShiftActive,
    shiftStartDate,
} = useMobileWorkday();
const { userProfile } = useMobileUser();
const { startTour, lastTourIndex } = useMobileTour();

const currentTime = ref(Date.now());
let intervalId = null;
const showHelpDialog = ref(false);

const connectOptions = [
    {
        label: "Email me",
        description: "Send your question by email",
        imageSrc: "/images/mobile/email.svg",
        type: "email",
        onClick: () => window.open("mailto:marc@kaamfu.ai"),
    },
    {
        label: "Call me",
        description: "Call and speak directly",
        imageSrc: "/images/mobile/phone-call.svg",
        type: "call",
        onClick: () => window.open("tel:+19548500243"),
    },
    {
        label: "Text message me",
        description: "Send a quick text",
        imageSrc: "/images/mobile/text-message.svg",
        type: "text",
        onClick: () => window.open("sms:+19548500243", "_blank"),
    },
    {
        label: "WhatsApp me",
        description: "Start a WhatsApp chat",
        imageSrc: "/images/mobile/whatsapp.svg",
        type: "whatsapp",
        onClick: () => window.open("https://wa.me/19548500243", "_blank"),
    },
    {
        label: "Book a meeting with me",
        description: "Schedule a 1:1 meeting",
        imageSrc: "/images/mobile/book-a-meeting.svg",
        type: "meeting",
        onClick: () =>
            window.open(
                "https://calendly.com/marcragsdale/1-on-1-with-marc",
                "_blank",
            ),
    },
];

const formatTime = (totalSeconds) => {
    if (
        !totalSeconds ||
        totalSeconds < 0 ||
        !Number.isFinite(totalSeconds) ||
        isNaN(totalSeconds)
    ) {
        return "00:00";
    }

    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);

    if (!Number.isFinite(hours) || !Number.isFinite(minutes)) {
        return "00:00";
    }

    return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}`;
};

const calculateElapsedSeconds = (startDate) => {
    if (!startDate) return 0;
    const start = new Date(startDate);
    const now = new Date(currentTime.value);
    const diffMs = now - start;
    return Math.floor(diffMs / 1000);
};

const timerDisplay = computed(() => {
    if (
        isShiftPausedOrUnreachableOrInActive.value &&
        shiftInfo.value?.duration
    ) {
        const durationInSeconds = shiftInfo.value.duration * 60;
        return formatTime(durationInSeconds);
    }
    if (isShiftActive.value && shiftStartDate.value) {
        const elapsedSeconds = calculateElapsedSeconds(shiftStartDate.value);
        return formatTime(elapsedSeconds);
    }
    return null;
});

const isOwnerOrAdmin = computed(() => {
    return (
        userProfile.value?.permissions?.owner ||
        userProfile.value?.permissions?.admin
    );
});
const workdayStatus = computed(() => {
    const status = officeHourStatus.value;
    const object = {
        [OFFICE_HOUR_STATUS.OFFICE_OPEN]: OfficeOpenIcon,
        [OFFICE_HOUR_STATUS.OFFICE_CLOSE]: OfficeCloseIcon,
        [OFFICE_HOUR_STATUS.PAST_OFFICE_HOURS]: PastOfficeHourIcon,
    };
    return object[status];
});
const profileMenuUserActions = computed(() => {
    const actions = [];
    if (isOwnerOrAdmin.value) {
        actions.push({
            label: "Guided Tour",
            icon: "mdi-compass-outline",
            onClick: startWorkdayTour,
            disabled: false,
        });
    }
    actions.push({
        label: "Get Support",
        icon: "sym_r_support",
        onClick: getSupport,
        disabled: false,
    });
    return actions;
});
const isShiftPaused = computed(() => {
    return (
        isShiftPausedOrUnreachableOrInActive.value &&
        !!shiftInfo.value?.duration
    );
});

function navigateToWorkday() {
    if (!isOwnerOrAdmin.value) {
        return;
    }
    router.push({ name: ROUTE_NAMES.MOBILE_WORKDAY });
}

async function startWorkdayTour() {
    const lastTourStep = lastTourIndex.value;
    startTour(lastTourStep);
}

function getSupport() {
    router.push({ name: ROUTE_NAMES.MOBILE_GET_HELP });
}

function handleConnectWithMarc(option) {
    option?.onClick?.();
    const payload = {
        type: MOBILE_ANALYTICS_TYPES.URL_CONNECT_WITH_MARC_MOBILE,
        data: {
            button: option.type,
        },
    };
    MobileAnalyticsTracker.track(payload);
}
onMounted(() => {
    intervalId = setInterval(() => {
        currentTime.value = Date.now();
    }, 1000);
});

onUnmounted(() => {
    if (intervalId) {
        clearInterval(intervalId);
    }
});
</script>

<template>
    <header
        class="mobile_header w-100 d-flex align-center justify-space-between position-sticky bg-section"
    >
        <div class="d-flex align-center">
            <div class="d-flex align-center mr-2">
                <component
                    :is="workdayStatus"
                    class="office-status-icon"
                    :size="29"
                />
            </div>
            <button
                class="mobile_header_pill rounded-pill d-flex align-center justify-space-between text-uppercase workday-pill workday-pill-tour-step bg-section"
                @click="navigateToWorkday"
            >
                <div class="d-flex align-center justify-center mobile_timer">
                    <template v-if="timerDisplay">
                        <span
                            class="mobile_timer_text"
                            :class="[
                                isShiftPaused ? 'text-pause' : 'text-success',
                            ]"
                            >{{ timerDisplay }}</span
                        >
                        <KIconButton
                            v-if="isShiftPaused"
                            noHover
                            rounded
                            active
                            iconClass="text-white"
                            activeClass="bg-pause"
                            size="25"
                            iconSize="20px"
                            iconActiveColor="#ffffff"
                            iconHoverColor="#ffffff"
                            icon="sym_o_pause"
                        />
                        <KIconButton
                            v-else
                            noHover
                            rounded
                            active
                            iconClass="text-white"
                            activeClass="bg-success"
                            size="25"
                            iconSize="20px"
                            iconActiveColor="#ffffff"
                            iconHoverColor="#ffffff"
                            icon="sym_o_play_arrow"
                        />
                    </template>
                    <template v-else>
                        <span class="mobile_header_pill_text text-pauseDark"
                            >Shift</span
                        >
                        <KIconButton
                            noHover
                            iconClass="text-pauseDark"
                            iconColor="pauseDark"
                            icon="sym_o_play_arrow"
                            size="25"
                            iconSize="20px"
                        />
                    </template>
                </div>
            </button>
        </div>

        <div class="d-flex align-center profile-controls-tour-step">
            <p
                class="text-fontLight text-uppercase mr-2 smaller-font"
                @click="showHelpDialog = true"
            >
                Help
            </p>
            <KProfileMobileMenu
                location="bottom end"
                :offset="[0, 8]"
                min-width="180px"
                show-header
            >
                <template #activator>
                    <div
                        class="mobile_header_pill mobile_header_pill--profile d-flex align-center justify-space-between rounded-pill cursor-pointer bg-accent profile-menu-actions"
                    >
                        <span
                            class="mobile_organization_name text-truncate text-fontLight"
                        >
                            {{ userProfile?.organization?.name }}
                        </span>

                        <KProfile
                            size="m-medium"
                            class="mobile_avatar"
                            :image-src="userProfile?.avatar_url"
                            :name="userProfile?.full_name"
                        />
                    </div>
                </template>

                <template #content>
                    <div>
                        <div
                            class="k_profile_menu_user d-flex align-center bg-section"
                        >
                            <KProfile
                                size="l-medium"
                                :image-src="userProfile?.avatar_url"
                                :name="userProfile?.full_name"
                            />
                            <div
                                class="d-flex flex-column align-start user_info_text"
                            >
                                <span class="user_name text-fontLight">
                                    {{ userProfile?.full_name }}
                                </span>
                                <span
                                    class="user_email text-decoration-none text-fontDimmed"
                                >
                                    {{ userProfile?.email }}
                                </span>
                            </div>
                        </div>

                        <KDivider />

                        <div class="k_profile_menu_actions">
                            <MobileOptionsMenuItem
                                v-for="action in profileMenuUserActions"
                                :key="action.label"
                                :icon="action.icon"
                                :disabled="action.disabled"
                                class="pa-4"
                                :class="[
                                    action.class,
                                    {
                                        'k_profile_menu_action--disabled':
                                            action.disabled,
                                    },
                                ]"
                                @click="
                                    !action.disabled && action.onClick
                                        ? action.onClick()
                                        : null
                                "
                            >
                                {{ action.label }}
                            </MobileOptionsMenuItem>
                        </div>
                    </div>
                </template>
            </KProfileMobileMenu>
        </div>
    </header>
    <KMobileDialog v-model="showHelpDialog" bodyClass="pa-0">
        <template #title>
            <div class="d-flex align-center ga-2">
                <KIcon
                    icon="sym_r_help"
                    :size="24"
                    color="rgb(var(--v-theme-icon))"
                />
                <span>Connect now with Marc</span>
            </div>
        </template>
        <template #content>
            <div class="help-connect">
                <div class="d-flex align-center ga-4 help-connect-profile-info">
                    <div
                        class="help-connect-avatar position-relative flex-shrink-0"
                    >
                        <KProfile
                            size="m-large"
                            image-src="/images/mobile/marc-300x300.png"
                            name="Marc"
                        />
                        <span
                            class="help-connect-online-dot position-absolute bg-success rounded-circle"
                        ></span>
                    </div>
                    <div>
                        <p
                            class="help-connect-question text-fontLight font-weight-bold"
                        >
                            How would you like to connect?
                        </p>
                        <p class="help-connect-subtitle text-fontDimmed">
                            Pick the channel that works best for you.
                        </p>
                    </div>
                </div>

                <div class="help-connect-options">
                    <template
                        v-for="(option, index) in connectOptions"
                        :key="option.label"
                    >
                        <KDivider v-if="index > 0" />
                        <div
                            class="help-connect-option d-flex align-center ga-2 cursor-pointer"
                            @click="handleConnectWithMarc(option)"
                        >
                            <div
                                class="help-connect-option-icon d-flex align-center justify-center rounded flex-shrink-0"
                            >
                                <img
                                    :src="option.imageSrc"
                                    :alt="option.label"
                                    class="help-connect-option-img"
                                />
                            </div>
                            <div>
                                <p
                                    class="help-connect-option-label text-fontLight font-weight-bold"
                                >
                                    {{ option.label }}
                                </p>
                                <p
                                    class="help-connect-option-desc text-fontDimmed"
                                >
                                    {{ option.description }}
                                </p>
                            </div>
                        </div>
                    </template>
                </div>
            </div>
        </template>
    </KMobileDialog>
</template>

<style lang="scss" scoped>
@import "../../../../sass/_theme-variables.sass";

.mobile_header {
    border-bottom: 1px solid $sectionBorder;
    min-height: 4rem;
    padding: 0 1rem;
    top: 0;
    z-index: 100;

    &:has(.k_profile_menu) {
        z-index: 102;
    }

    .mobile_header_pill {
        border: 1px solid $sectionBorder;
        font-size: 14px;
        letter-spacing: normal;
        font-weight: 500;
        height: 2.5rem;
        padding-right: 0.25rem;
        cursor: pointer;

        &--profile {
            border-color: $sectionBorder;
            max-width: 150px;
            gap: 0.75rem;
            padding-left: 1rem;
        }

        .mobile_header_pill_text {
            font-size: 14px;
            font-weight: 500;
            letter-spacing: normal;
            margin-right: 0.5rem;
        }

        .mobile_timer {
            min-width: 20px;

            .mobile_timer_text {
                font-size: 14px;
                font-weight: 500;
                letter-spacing: normal;
                margin-right: 0.5rem;
            }

            .mobile_play_icon {
                font-size: 18px;
                line-height: 1;
            }
        }

        .mobile_organization_name {
            font-size: 14px;
            font-weight: 500;
            max-width: 100px;
            text-transform: none;
            letter-spacing: normal;
        }

        .mobile_avatar {
            width: 28px;
            height: 28px;
            border-radius: 50%;
        }
    }
    .workday-pill {
        padding-left: 0.75rem;
        padding-right: 0.5rem;
        gap: 0.5rem;
    }
}
.smaller-font {
    font-size: 14px;
    font-weight: 500;
    letter-spacing: normal;
}

.help-connect {
    &-profile-info {
        padding: 1.5rem;
    }
    &-avatar {
        .help-connect-online-dot {
            width: 1.25rem;
            height: 1.25rem;
            border: 2px solid rgb(var(--v-theme-section));
            top: 20px;
            left: -10px;
        }
    }

    &-question {
        font-size: 14px;
        line-height: 1.4;
        margin-bottom: 2px;
    }

    &-subtitle {
        font-size: 13px;
        line-height: 1.4;
    }

    &-option {
        padding: 0.75rem 1rem;

        .help-connect-option-icon {
            width: 42px;
            height: 42px;
        }

        .help-connect-option-img {
            width: 24px;
            height: 24px;
            object-fit: contain;
        }

        .help-connect-option-label {
            font-size: 14px;
            line-height: 1.3;
            margin-bottom: 2px;
        }

        .help-connect-option-desc {
            font-size: 12px;
            line-height: 1.3;
        }
    }
}
</style>
<style lang="scss">
@import "../../../../sass/_theme-variables.sass";
.k_profile_menu_user {
    padding: 1rem;
    gap: 0.75rem;

    .user_info_text {
        flex: 1;
        min-width: 0;

        .user_name {
            font-size: 14px;
            font-weight: 700;
            line-height: 1.5;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            max-width: 150px;
        }

        .user_email {
            font-size: 12px;
            font-weight: 400;
            line-height: 1.5;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            max-width: 150px;
        }
    }
}

.k_profile_menu_actions {
    .k_profile_menu_actions_label {
        padding: 0.5rem 1rem;
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
    }

    .k_profile_menu_action--disabled {
        opacity: 0.5;
        cursor: not-allowed;
        pointer-events: none;
    }
}
</style>
